import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { countBy, get, groupBy, keyBy } from 'lodash';
import { httpsCallable } from 'firebase/functions';
import { updateDoc, collection, query, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { useToggle } from 'react-use';

import { db, functions } from '../../firebase';
import env from '../../env';
import { fullPathWithParams } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../AdminPage';
import ProgressButton from '../ProgressButton';
import QuerySelector from '../QuerySelector';

const STRIPE_ACCOUNT_ID = 'acct_1T49bNBUrNyNqYTb';

export function stripeCustomerUrl(customerId) {
  const isProduction = import.meta.env.VITE_ENV === 'production';
  const pathPrefix = isProduction ? '' : '/test';
  return `https://dashboard.stripe.com/${STRIPE_ACCOUNT_ID}${pathPrefix}/customers/${customerId}`;
}

const subscriptionStatusLabels = {
  trialing: { label: 'トライアル中', color: 'info' },
  active: { label: '有効', color: 'success' },
  past_due: { label: '支払い遅延', color: 'warning' },
  unpaid: { label: '未払い', color: 'danger' },
  canceled: { label: 'キャンセル済み', color: 'secondary' },
  incomplete: { label: '未完了', color: 'warning' },
  incomplete_expired: { label: '期限切れ', color: 'secondary' },
  paused: { label: '一時停止中', color: 'secondary' },
};

export function SubscriptionStatusBadge({ status }) {
  if (!status) return <span className="text-muted">—</span>;
  const setting = subscriptionStatusLabels[status] ?? { label: status, color: 'secondary' };
  return <span className={`badge bg-${setting.color}`}>{setting.label}</span>;
}

export default function AdminTenants() {
  return (
    <AdminPage>
      <PageContent />
    </AdminPage>
  );
}

const STATUS_FILTER_OPTIONS = [
  { value: 'trialing', label: 'トライアル中' },
  { value: 'active', label: '有効' },
  { value: 'past_due', label: '支払い遅延' },
  { value: 'unpaid', label: '未払い' },
  { value: 'canceled', label: 'キャンセル済み' },
  { value: 'incomplete', label: '未完了' },
  { value: 'incomplete_expired', label: '期限切れ' },
  { value: 'paused', label: '一時停止中' },
  { value: 'none', label: '未契約' },
];

function PageContent(props) {
  const { user } = props;
  const location = useLocation();
  const { subscriptionStatus: statusFilter, referrer: referrerFilter } = useQueryParams();
  const tenants = useCollectionSubscription(query(collection(db, 'tenants'), orderBy('createdAt', 'desc')));
  const users = useCollectionSubscription(query(collection(db, 'users')));
  const usersGroupedByTenantId = groupBy(users, 'tenantId');
  const tenantsGroupedByReferrerTenantId = groupBy(tenants, 'referrerTenantId');
  const tenantsById = keyBy(tenants, 'id');

  const statusCounts = countBy(tenants, _ => _.subscriptionStatus || 'none');
  const statusOptions = STATUS_FILTER_OPTIONS.map(_ => ({ ..._, label: `${_.label} (${statusCounts[_.value] || 0}件)` }));

  const referrerOptions = tenants
    .filter(_ => _.affiliateCode)
    .map(_ => ({ value: _.id, label: `${_.name || _.id} (紹介: ${(tenantsGroupedByReferrerTenantId[_.id] || []).length}件)` }));

  const statusFilteredTenants = !statusFilter
    ? tenants
    : tenants.filter(_ => (_.subscriptionStatus || 'none') === statusFilter);
  const filteredTenants = referrerFilter
    ? statusFilteredTenants.filter(_ => _.referrerTenantId === referrerFilter)
    : statusFilteredTenants;

  return (
    <div className="admin-tenants container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>テナント管理</h4>
        </div>
        <div className="d-flex gap-3 mb-3 flex-wrap">
          <QuerySelector
            paramName="subscriptionStatus"
            label="サブスク状況"
            width={240}
            options={statusOptions}
          />
          <QuerySelector
            paramName="referrer"
            label="紹介者"
            width={320}
            options={referrerOptions}
          />
        </div>
        <div className="text-muted small mb-2">
          表示中: {filteredTenants.length} / {tenants.length} 件
        </div>
        <div className="overflow-scroll">
          <table className="table">
            <thead className="thead-light text-center">
              <tr className="text-nowrap">
                <th>ID</th>
                <th>テナント名</th>
                <th>ユーザーメールアドレス</th>
                <th>サブスク状況</th>
                <th>作成日時</th>
                <th>紹介者</th>
                <th>アフィリエイト</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {
                filteredTenants.map((tenant) => {
                  const { id, name, createdAt, stripeCustomerId, subscriptionStatus, referrerTenantId } = tenant;
                  const users = usersGroupedByTenantId[id];
                  const referredTenants = tenantsGroupedByReferrerTenantId[id] || [];
                  const referrer = referrerTenantId ? tenantsById[referrerTenantId] : null;
                  const changeTenant = async () => {
                    if(!window.confirm(`${name}のユーザーになります。よろしいですか？`)) return;

                    await updateDoc(user.ref, { tenantId: id });
                  };

                  return (
                    <tr key={id}>
                      <td>
                        {id}
                        {
                          user.dev && (
                            <a className="d-block" href={`https://console.firebase.google.com/u/0/project/${env('FIREBASE_PROJECT_ID')}/firestore/data/~2Ftenants~2F${id}`} target="_blank">
                              F
                            </a>
                          )
                        }
                      </td>
                      <td>
                        {name}
                      </td>
                      <td>
                        {users?.map(_ => _.email).join(', ')}
                      </td>
                      <td>
                        <SubscriptionStatusBadge status={subscriptionStatus} />
                        {stripeCustomerId && (
                          <a
                            className="d-block small"
                            href={stripeCustomerUrl(stripeCustomerId)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Stripeで見る
                          </a>
                        )}
                      </td>
                      <td>
                        {createdAt?.toDate().toLocaleString('ja-JP')}
                      </td>
                      <td>
                        {referrer ? (
                          <Link to={fullPathWithParams({ referrer: referrer.id }, location)}>
                            {referrer.name || referrer.id}
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <AffiliateButton tenant={tenant} />
                          {tenant.affiliateCode && (
                            <Link to={fullPathWithParams({ referrer: tenant.id }, location)} className="small text-nowrap">
                              紹介: {referredTenants.length}件
                            </Link>
                          )}
                        </div>
                      </td>
                      <td>
                        <ProgressButton label="このテナントのユーザーになる" process={changeTenant} disabled={user.tenantId === id} />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function affiliateLinkFor(code) {
  return `${window.location.origin}/r/${code}`;
}

function AffiliateButton({ tenant }) {
  const [showsModal, toggleModal] = useToggle(false);
  const [isIssuing, toggleIssuing] = useToggle(false);
  const { affiliateCode } = tenant;

  const issueCode = async () => {
    const confirmMessage = affiliateCode
      ? 'コードを再発行します。既存のリンクは無効になります。よろしいですか？'
      : 'アフィリエイトコードを発行します。よろしいですか？';
    if (!window.confirm(confirmMessage)) return;
    toggleIssuing(true);
    try {
      const issueAffiliateCode = httpsCallable(functions, 'issueAffiliateCode');
      await issueAffiliateCode({ tenantId: tenant.id });
      toast.success(affiliateCode ? '再発行しました' : '発行しました');
    } catch (e) {
      console.error(e);
      toast.error('発行に失敗しました');
    }
    toggleIssuing(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(affiliateLinkFor(affiliateCode));
      toast.success('リンクをコピーしました');
    } catch (e) {
      console.error(e);
      toast.error('コピーに失敗しました');
    }
  };

  return (
    <>
      <Button color="secondary" outline size="sm" onClick={toggleModal} className="text-nowrap">
        {affiliateCode ? 'コード' : '未発行'}
      </Button>
      {showsModal && (
        <Modal isOpen>
          <ModalHeader>アフィリエイト — {tenant.name}</ModalHeader>
          <ModalBody>
            <div className="fw-bold mb-2">アフィリエイトリンク</div>
            {affiliateCode ? (
              <>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <input className="form-control" readOnly value={affiliateLinkFor(affiliateCode)} />
                  <Button color="primary" outline onClick={copyLink} className="text-nowrap flex-shrink-0">コピー</Button>
                </div>
                <div className="text-muted small">コード: {affiliateCode}</div>
              </>
            ) : (
              <div className="text-muted">まだ発行されていません</div>
            )}
            <div className="mt-3">
              <Button color="primary" onClick={issueCode} disabled={isIssuing}>
                {isIssuing ? '発行中...' : (affiliateCode ? '再発行する' : '発行する')}
              </Button>
              {affiliateCode && (
                <span className="text-muted small ms-2">※再発行すると既存のリンクは無効になります</span>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={toggleModal}>閉じる</Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
